import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Package,
  Pencil,
  Plus,
  Save,
  Search,
  Tag,
  FileText,
  IndianRupee,
  ToggleLeft,
  Trash2,
  X,
} from "lucide-react";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "./documentSharedStyles";
import {
  usePortaledActionMenu,
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackChip,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  EntriesControl,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  ModalOverlay,
  ModalShell,
  ModalHero,
  ModalHeroGlow,
  ModalHeroRow,
  ModalHeroText,
  ModalHeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldTextArea,
  FieldSelect,
  InlineSelect,
  ModalActions,
  SecondaryButton,
  PrimaryButton,
  StatusConfirmOverlay,
  StatusConfirmBox,
  StatusBody,
  StatusCancelBtn,
  StatusConfirmBtn,
} from "./quotationMasterListShared";
import { SubmitButton, FormLabelIcon, FormGradientDefs } from "../../molecules/Dashboard/Member/memberFormStyles";
import ExportDropdown from "../../atoms/Dashboard/ExportDropdown";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { getApiErrorMessage } from "../../atoms/Utlis/getApiErrorMessage";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import { formatINR } from "../../../Utils/helper";
import { useAppSelector } from "../../../Redux/Hooks";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { ListReportPrintTemplate, ReportStatusBadge, CellBold } from "./ListReportPrintTemplate";
import {
  getProductsAPI,
  createProductAPI,
  updateProductAPI,
  deleteProductAPI,
} from "../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
  ClickableStatusPill,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";

const ScrollPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const ScrollPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const ScrollTable = styled(PanelTableScroll)`
  flex: none;
  overflow-y: visible;
  min-height: unset;
`;

type QuotationProduct = {
  id: number;
  name: string;
  description: string;
  product_code?: string;
  price?: number;
  status?: string;
  created_at: string;
  branch_id?: number | null;
  branch_name?: string | null;
};

const QuotationProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, branches, selectedBranch } = useAppSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("quotation.update");
  const canDelete = hasPermission("quotation.delete");

  const reportPrintRef = useRef<HTMLDivElement>(null);
  const [productsList, setProductsList] = useState<QuotationProduct[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<QuotationProduct | null>(null);
  const {
    activeMenuId,
    menuCoords,
    menuButtonRefs,
    scrollRef,
    handleToggleMenu,
    closeMenu,
  } = usePortaledActionMenu(140);
  const [statusConfirmProduct, setStatusConfirmProduct] = useState<QuotationProduct | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [branchId, setBranchId] = useState<string | number>(selectedBranch?.id || "");
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [productCode, setProductCode] = useState("");
  const [productPrice, setProductPrice] = useState<number | string>("");
  const [status, setStatus] = useState("active");

  const [totalCount, setTotalCount] = useState(0);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await getProductsAPI({
        page: currentPage,
        page_size: entries,
        search: searchText,
        branch_id: selectedBranch?.id
      });
      setProductsList(res?.results || []);
      setTotalCount(res?.count || 0);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [currentPage, entries, searchText, selectedBranch?.id]);

  const openAddModal = () => {
    setEditingProduct(null);
    setBranchId(selectedBranch?.id || "");
    setProductName("");
    setDescription("");
    setProductCode(`PRD-${Math.floor(100000 + Math.random() * 900000)}`);
    setProductPrice("");
    setStatus("active");
    setIsAddModalOpen(true);
  };

  const openEditModal = (product: QuotationProduct) => {
    setEditingProduct(product);
    setBranchId(product.branch_id || "");
    setProductName(product.name);
    setDescription(product.description || "");
    setProductCode(product.product_code || "");
    setProductPrice(product.price !== undefined && product.price !== null ? product.price : "");
    setStatus(product.status || "active");
    setIsAddModalOpen(true);
  };

  const handleSaveProduct = async () => {
    if (isSaving) return;
    if (!productName.trim()) {
      showErrorToast("Product name is required");
      return;
    }
    setIsSaving(true);
    const data = {
      name: productName,
      description,
      product_code: productCode,
      price: productPrice !== "" ? Number(productPrice) : null,
      status,
      branch_id: branchId ? Number(branchId) : null,
    };

    try {
      if (editingProduct) {
        await updateProductAPI(editingProduct.id, data);
        showSuccessToast("Product updated successfully!");
      } else {
        await createProductAPI(data);
        showSuccessToast("Product created successfully!");
      }
      setIsAddModalOpen(false);
      fetchProducts();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to save product"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId === null || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteProductAPI(deleteConfirmId);
      showSuccessToast("Product deleted successfully!");
      fetchProducts();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to delete product"));
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const handleCopy = () => {
    copyDataToClipboard(
      productsList,
      ["ID", "Product Name", "Code", "Status"],
      ["id", "name", "product_code", "status"],
      "Product list copied to clipboard"
    );
  };

  const handleExportCSV = () => {
    exportDataToCSV(
      productsList,
      ["ID", "Product Name", "Code", "Status", "Created At"],
      ["id", "name", "product_code", "status", "created_at"],
      "products_export.csv"
    );
  };

  const handleExportExcel = () => {
    exportDataToExcel(
      productsList,
      ["ID", "Product Name", "Code", "Status", "Created At"],
      ["id", "name", "product_code", "status", "created_at"],
      "products_export.xls"
    );
  };

  const productReportRows = useMemo(
    () =>
      productsList.map((p) => ({
        name: p.name,
        product_code: p.product_code || "—",
        price:
          p.price !== undefined && p.price !== null
            ? `₹${formatINR(p.price)}`
            : "—",
        price_raw: Number(p.price) || 0,
        status: p.status === "inactive" ? "Inactive" : "Active",
        status_key: p.status || "active",
        created_at: p.created_at
          ? new Date(p.created_at).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—",
      })),
    [productsList]
  );

  const productReportSummary = useMemo(() => {
    const activeCount = productReportRows.filter((r) => r.status_key !== "inactive").length;
    const inactiveCount = productReportRows.length - activeCount;
    const totalValue = productReportRows.reduce((sum, row) => sum + row.price_raw, 0);
    return [
      { label: "Total Products", value: String(productReportRows.length) },
      { label: "Active", value: String(activeCount) },
      { label: "Inactive", value: String(inactiveCount) },
      { label: "Total Catalog Value", value: `₹${formatINR(totalValue)}`, highlight: true },
    ];
  }, [productReportRows]);

  const getReportStatusStyle = (status: string) => {
    if (status === "inactive") {
      return { bg: "rgba(23, 23, 23, 0.1)", color: "rgba(23, 23, 23, 0.65)" };
    }
    return { bg: "rgba(49, 166, 106, 0.15)", color: "#2d8a55" };
  };

  const handleExportPDF = async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch (err) {
      showErrorToast("Failed to generate PDF");
    }
  };

  const visibleProducts = productsList;
  const totalFilteredCount = totalCount;
  const totalPages = Math.ceil(totalFilteredCount / entries) || 1;

  return (
    <ScrollPage>
      {isAddModalOpen &&
        createPortal(
          <>
            <FormGradientDefs />
            <ModalOverlay onClick={() => setIsAddModalOpen(false)}>
              <ModalShell style={{ maxWidth: 520 }} onClick={(event) => event.stopPropagation()}>
                <ModalHero>
                  <ModalHeroGlow />
                  <ModalHeroRow>
                    <ModalHeroText>
                      <ModalHeroBadge>
                        {editingProduct ? <Pencil size={11} /> : <Plus size={11} />}
                        {editingProduct ? "Edit product" : "New product"}
                      </ModalHeroBadge>
                      <ModalTitle>{editingProduct ? "Edit Product" : "Add New Product"}</ModalTitle>
                      <ModalSubtitle>
                        {editingProduct
                          ? "Update product details and pricing."
                          : "Create a static quotation product entry."}
                      </ModalSubtitle>
                    </ModalHeroText>
                    <CloseBtn type="button" onClick={() => setIsAddModalOpen(false)} aria-label="Close">
                      <X size={18} />
                    </CloseBtn>
                  </ModalHeroRow>
                </ModalHero>

                <ModalBody>
                  <FormCard>
                    {branches && branches.length > 0 && (
                      <FieldGroup>
                        <FieldLabel>
                          Branch (Optional)
                        </FieldLabel>
                        <FieldSelect value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                          <option value="">All Branches / Main Studio (HQ)</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.branch_name} {b.city ? `(${b.city})` : ''}
                            </option>
                          ))}
                        </FieldSelect>
                      </FieldGroup>
                    )}
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={Tag} />
                        Product Name
                      </FieldLabel>
                      <FieldInput
                        placeholder="Enter product name"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={FileText} />
                        Description
                      </FieldLabel>
                      <FieldTextArea
                        placeholder="Enter product description"
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={IndianRupee} />
                        Price (₹)
                      </FieldLabel>
                      <FieldInput
                        type="number"
                        placeholder="Enter price"
                        value={productPrice}
                        onChange={(e) => setProductPrice(e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={ToggleLeft} />
                        Status
                      </FieldLabel>
                      <FieldSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </FieldSelect>
                    </FieldGroup>
                  </FormCard>
                </ModalBody>

                <ModalActions>
                  <PrimaryButton type="button" onClick={handleSaveProduct} disabled={isSaving}>
                    <Save size={16} />
                    {isSaving ? "Saving..." : editingProduct ? "Update Product" : "Save Product"}
                  </PrimaryButton>
                  <SecondaryButton type="button" onClick={() => setIsAddModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                </ModalActions>
              </ModalShell>
            </ModalOverlay>
          </>,
          document.body
        )}

      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackChip type="button" onClick={() => navigate("/dashboard/quotation/")} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackChip>
            <HeroText>
              <HeroBadge><Package size={11} /> Quotation masters</HeroBadge>
              <PageTitle className="linear-text">All Products</PageTitle>
              <PageSubtitle>Manage quotation product masters and pricing items.</PageSubtitle>
            </HeroText>
          </HeroLeft>
          <PermissionGate permission="quotation.create">
            <SubmitButton type="button" $ready onClick={openAddModal} style={{ minWidth: 180, height: 46 }}>
              <Plus size={16} /> Add New Product
            </SubmitButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <ScrollPanel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                value={searchVal}
                onChange={(event) => setSearchVal(event.target.value)}
                placeholder="Search products"
              />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <InlineSelect value={entries} onChange={(event) => { setEntries(Number(event.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </InlineSelect>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>


        <ScrollTable ref={scrollRef}>
          <TableWrap>
            <PaymentsTable $minWidth={860}>
            <thead>
              <tr>
                <th><span>Name</span></th>
                <th><span>Branch</span></th>
                <th><span>Description</span></th>
                <th><span>Price (₹)</span></th>
                <th><span>Status</span></th>
                <th><span>Created On</span></th>
                <th><span>Action</span></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: "30px" }}>
                    Loading products...
                  </td>
                </tr>
              ) : visibleProducts.length > 0 ? (
                visibleProducts.map((product, idx) => (
                  <tr key={product.id}>
                    <td>{product.name}</td>
                    <td>
                      {product.branch_name ? (
                        <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                          {product.branch_name}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                    <td>{product.description || "-"}</td>
                    <td>
                      {product.price !== undefined && product.price !== null ? (
                        <AmountCell>₹{product.price}</AmountCell>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <ClickableStatusPill
                        type="button"
                        $status={product.status || "active"}
                        onClick={canUpdate ? () => setStatusConfirmProduct(product) : undefined}
                        style={{ cursor: canUpdate ? "pointer" : "default" }}
                      >
                        {product.status === "inactive" ? "Inactive" : "Active"}
                      </ClickableStatusPill>
                    </td>
                    <td>{product.created_at ? new Date(product.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "-"}</td>
                    <td>
                      {(canUpdate || canDelete) && (
                        <MenuContainer>
                          <ThreeDotsButton
                            ref={(el) => { menuButtonRefs.current[product.id] = el; }}
                            type="button"
                            aria-expanded={activeMenuId === product.id}
                            onClick={(e) => { e.stopPropagation(); handleToggleMenu(product.id); }}
                          >
                            <Dot /><Dot /><Dot />
                          </ThreeDotsButton>
                        </MenuContainer>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "30px" }}>
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
            </PaymentsTable>
          </TableWrap>
        </ScrollTable>

        <FooterRow>
          <FooterText>
            Showing {totalFilteredCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalFilteredCount)} of {totalFilteredCount} entries
          </FooterText>
          <Pagination>
            <PageButton
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </PageButton>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <PageButton
                key={idx}
                $active={currentPage === idx + 1}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </PageButton>
            ))}
            <PageButton
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </PageButton>
          </Pagination>
        </FooterRow>
      </ScrollPanel>

      {statusConfirmProduct !== null &&
        createPortal(
          <>
            <FormGradientDefs />
            <StatusConfirmOverlay onClick={() => setStatusConfirmProduct(null)}>
              <StatusConfirmBox onClick={(e) => e.stopPropagation()}>
                <ModalHero>
                  <ModalHeroGlow />
                  <ModalHeroRow>
                    <ModalHeroText>
                      <ModalHeroBadge>
                        <ToggleLeft size={11} />
                        Status change
                      </ModalHeroBadge>
                      <ModalTitle>Change Status?</ModalTitle>
                      <ModalSubtitle>Update the active state for this product.</ModalSubtitle>
                    </ModalHeroText>
                    <CloseBtn
                      type="button"
                      onClick={() => setStatusConfirmProduct(null)}
                      aria-label="Close"
                    >
                      <X size={18} />
                    </CloseBtn>
                  </ModalHeroRow>
                </ModalHero>
                <ModalBody>
                  <StatusBody>
                    Are you sure you want to change status to{" "}
                    <strong>
                      {statusConfirmProduct.status === "active" ? "Inactive" : "Active"}
                    </strong>
                    ?
                  </StatusBody>
                </ModalBody>
                <ModalActions>
                  <StatusConfirmBtn
                    type="button"
                    onClick={async () => {
                      if (!statusConfirmProduct) return;
                      const newStatus =
                        statusConfirmProduct.status === "active" ? "inactive" : "active";
                      try {
                        await updateProductAPI(statusConfirmProduct.id, {
                          name: statusConfirmProduct.name,
                          description: statusConfirmProduct.description,
                          product_code: statusConfirmProduct.product_code,
                          status: newStatus,
                        });
                        showSuccessToast(`Product status updated to ${newStatus}!`);
                        fetchProducts();
                      } catch (err: any) {
                        showErrorToast(getApiErrorMessage(err, "Failed to update status"));
                      } finally {
                        setStatusConfirmProduct(null);
                      }
                    }}
                  >
                    Change
                  </StatusConfirmBtn>
                  <StatusCancelBtn type="button" onClick={() => setStatusConfirmProduct(null)}>
                    Cancel
                  </StatusCancelBtn>
                </ModalActions>
              </StatusConfirmBox>
            </StatusConfirmOverlay>
          </>,
          document.body
        )}

      {activeMenuId !== null && menuCoords && (() => {
        const product = visibleProducts.find(p => p.id === activeMenuId);
        if (!product) return null;
        return createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu $fixed style={{ top: menuCoords.top, bottom: menuCoords.bottom, right: menuCoords.right }} onClick={(e) => e.stopPropagation()}>
              {canUpdate && (
                <MenuItem type="button" onClick={() => { closeMenu(); openEditModal(product); }}>
                  <ItemIconContainer><Pencil size={14} /></ItemIconContainer>
                  <ItemLabel>Edit</ItemLabel>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem type="button" onClick={() => { closeMenu(); setDeleteConfirmId(product.id); }}>
                  <ItemIconContainer><Trash2 size={14} /></ItemIconContainer>
                  <ItemLabel>Delete</ItemLabel>
                </MenuItem>
              )}
            </DropdownMenu>
          </>,
          document.body
        );
      })()}

      {deleteConfirmId !== null && (
        <DeletePopup
          text="Are you sure you want to delete this product?"
          buttonText="Delete"
          cancel={() => setDeleteConfirmId(null)}
          Delete={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Products Report"
        subtitle="Quotation products catalog overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: "name", label: "Product Name", width: "28%" },
          { key: "product_code", label: "Code", width: "16%" },
          {
            key: "price",
            label: "Price",
            align: "right",
            width: "14%",
            render: (value) => <CellBold>{String(value)}</CellBold>,
          },
          {
            key: "status",
            label: "Status",
            align: "center",
            width: "14%",
            render: (value, row) => {
              const style = getReportStatusStyle(String(row.status_key || ""));
              return (
                <ReportStatusBadge $bg={style.bg} $color={style.color}>
                  {String(value)}
                </ReportStatusBadge>
              );
            },
          },
          { key: "created_at", label: "Created On", width: "16%" },
        ]}
        rows={productReportRows}
        summaryItems={productReportSummary}
      />
    </ScrollPage>
  );
};

export default QuotationProductsPage;
